import * as Sentry from '@sentry/react';
import type { AssignmentHistoryResponse } from '../types';
import {
  AirtableModeration,
  BulkAssignmentResponse,
  BulkEligibilityAction,
  CandidateTranscription,
  CandidateTranscriptionFilters,
  Criteria,
  CriterionComparison,
  CriterionEligibilitySummary,
  EligibilityProgress,
  PolicyPin,
  PolicyVersionState,
  CriteriaPurpose,
  CriterionTranscriptionAssignment,
  DisagreementCandidate,
  DisagreementCountsResponse,
  Essay,
  EssayPurpose,
  GroundTruthCoverageFilters,
  GroundTruthCoverageResponse,
  Label,
  MarkingMetricsFilters,
  MarkingMetricsResponse,
  MarkingMetricsSettings,
  ModerationPolicySuggestion,
  OutstandingPolicyFeedback,
  Policy,
  PolicySuggestion,
  PolicySuggestionGenerateResult,
  Purpose,
  ReviewedOutput,
  StylusJudgement,
  StylusTranscriptionSummary,
  AiJudgementSource,
  ConsensusMode,
  EvalJudgementSourceCatalog,
  EvalMarkingRun,
  Transcription,
  User,
  UserVsGtCandidate,
  UserVsGtCountsResponse,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const DEV_USER = process.env.NODE_ENV !== 'production' ? process.env.REACT_APP_DEV_USER : undefined;

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type TokenProvider = (opts: { forceRefresh: boolean }) => Promise<string | null>;

let tokenProvider: TokenProvider | null = null;

export const setTokenProvider = (provider: TokenProvider) => {
  tokenProvider = provider;
};

const sendRequest = async (
  url: string,
  options: RequestInit,
  forceRefresh: boolean
): Promise<Response> => {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  if (DEV_USER) {
    headers['X-Dev-User'] = DEV_USER;
  } else if (tokenProvider) {
    const token = await tokenProvider({ forceRefresh });
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return fetch(url, { ...options, headers });
};

const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  let response = await sendRequest(url, options, false);
  if (response.status === 401 && !DEV_USER && tokenProvider) {
    response = await sendRequest(url, options, true);
  }
  if (!response.ok) {
    const method = options.method ?? 'GET';
    const body = await response.text();
    const error = new ApiError(
      response.status,
      body,
      `${method} ${url} failed: ${response.status}`
    );
    Sentry.captureException(error, {
      tags: { api_status: String(response.status), api_method: method },
      extra: { url, body },
    });
    throw error;
  }
  return response;
};

// User endpoints
export const fetchUsers = async (): Promise<User[]> => {
  const response = await authenticatedFetch(`${API_URL}/api/users`);
  return response.json();
};

export const updateUserRole = async (userId: string, userType: User['userType']): Promise<User> => {
  const response = await authenticatedFetch(`${API_URL}/api/users/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userType }),
  });
  return response.json();
};

// Essay endpoints
export const fetchEssays = async (): Promise<Essay[]> => {
  const response = await authenticatedFetch(`${API_URL}/api/essays`);
  return response.json();
};

// Policy endpoints
export const fetchPolicies = async (): Promise<Policy[]> => {
  const response = await authenticatedFetch(`${API_URL}/api/policies`);
  return response.json();
};

export const fetchPolicy = async (policyId: string): Promise<Policy> => {
  const response = await authenticatedFetch(`${API_URL}/api/policies/${policyId}`);
  return response.json();
};

export const createPolicy = async (
  policy: Omit<Policy, 'id' | 'creationDate' | 'modifiedDate'>
): Promise<Policy> => {
  const response = await authenticatedFetch(`${API_URL}/api/policies`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(policy),
  });
  return response.json();
};

// updatePolicy is deliberately absent: policies are immutable, so a rubric change
// creates a new policy via createPolicy. See docs/adr/0001-policy-versions-and-eval-eligibility.md#immutability.

// Label endpoints
export const fetchLabels = async (): Promise<Label[]> => {
  const response = await authenticatedFetch(`${API_URL}/api/labels`);
  return response.json();
};

export const createLabel = async (
  label: Omit<Label, 'id' | 'createdBy' | 'createdDate'>
): Promise<Label> => {
  const response = await authenticatedFetch(`${API_URL}/api/labels`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(label),
  });
  return response.json();
};

export const updateLabel = async (label: Label): Promise<Label> => {
  const response = await authenticatedFetch(`${API_URL}/api/labels/${label.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(label),
  });
  return response.json();
};

export const deleteLabel = async (labelId: string): Promise<{ success: boolean }> => {
  await authenticatedFetch(`${API_URL}/api/labels/${labelId}`, {
    method: 'DELETE',
  });
  return { success: true };
};

export const deleteLabelsBulk = async (labelIds: string[]): Promise<{ deleted: number }> => {
  const response = await authenticatedFetch(`${API_URL}/api/labels/bulk-delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ labelIds }),
  });
  return response.json();
};

// Purpose endpoints
export const fetchPurposes = async (): Promise<Purpose[]> => {
  const response = await authenticatedFetch(`${API_URL}/api/purposes`);
  return response.json();
};

export const fetchEssayPurposes = async (): Promise<EssayPurpose[]> => {
  const response = await authenticatedFetch(`${API_URL}/api/essay-purposes`);
  return response.json();
};

export const fetchCriteriaPurposes = async (): Promise<CriteriaPurpose[]> => {
  const response = await authenticatedFetch(`${API_URL}/api/criteria-purposes`);
  return response.json();
};

export const createCriteriaPurpose = async (
  criteriaPurpose: Omit<CriteriaPurpose, 'id' | 'creationDate'>
): Promise<CriteriaPurpose> => {
  const response = await authenticatedFetch(`${API_URL}/api/criteria-purposes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(criteriaPurpose),
  });
  return response.json();
};

export const deleteCriteriaPurpose = async (id: string): Promise<void> => {
  await authenticatedFetch(`${API_URL}/api/criteria-purposes/${id}`, {
    method: 'DELETE',
  });
};

// Criteria endpoints
export const fetchCriteria = async (): Promise<Criteria[]> => {
  const response = await authenticatedFetch(`${API_URL}/api/criteria`);
  return response.json();
};

export const createCriteria = async (
  criteria: Omit<Criteria, 'id' | 'creationDate' | 'modifiedDate'>
): Promise<Criteria> => {
  const response = await authenticatedFetch(`${API_URL}/api/criteria`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(criteria),
  });
  return response.json();
};

export const updateCriteria = async (
  id: string,
  criteria: Omit<Criteria, 'id' | 'creationDate' | 'modifiedDate'>
): Promise<Criteria> => {
  const response = await authenticatedFetch(`${API_URL}/api/criteria/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(criteria),
  });
  return response.json();
};

export const getCurrentUser = async (): Promise<User> => {
  const response = await authenticatedFetch(`${API_URL}/api/users/me`);
  return response.json();
};

// Transcription endpoints
export const fetchTranscriptions = async (): Promise<Transcription[]> => {
  const response = await authenticatedFetch(`${API_URL}/api/transcriptions`);
  return response.json();
};

export const fetchCriterionTranscriptionAssignments = async (): Promise<
  CriterionTranscriptionAssignment[]
> => {
  const response = await authenticatedFetch(`${API_URL}/api/criterion-transcription-assignments`);
  return response.json();
};

export const fetchUserCriterionTranscriptionAssignments = async (
  userId: string
): Promise<CriterionTranscriptionAssignment[]> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/criterion-transcription-assignments/user/${userId}`
  );
  return response.json();
};

export const fetchAssignmentHistory = async (
  criteriaId: string
): Promise<AssignmentHistoryResponse> => {
  const params = new URLSearchParams({ criteriaId });
  const response = await authenticatedFetch(
    `${API_URL}/api/criterion-transcription-assignments/history?${params.toString()}`
  );
  return response.json();
};

export const createCriterionTranscriptionAssignment = async (
  assignment: Omit<
    CriterionTranscriptionAssignment,
    'id' | 'assignedBy' | 'assignedDate' | 'status' | 'startedAt'
  >
): Promise<CriterionTranscriptionAssignment> => {
  const response = await authenticatedFetch(`${API_URL}/api/criterion-transcription-assignments`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(assignment),
  });
  return response.json();
};

export const deleteCriterionTranscriptionAssignment = async (
  assignmentId: string
): Promise<{ success: boolean }> => {
  await authenticatedFetch(`${API_URL}/api/criterion-transcription-assignments/${assignmentId}`, {
    method: 'DELETE',
  });
  return { success: true };
};

export const updateAssignmentDone = async (
  assignmentId: string,
  done: boolean
): Promise<CriterionTranscriptionAssignment> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/criterion-transcription-assignments/${assignmentId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ done }),
    }
  );
  return response.json();
};

export const bulkUpdateAssignmentsDone = async (
  assignmentIds: string[],
  done: boolean
): Promise<{ updatedCount: number; assignments: CriterionTranscriptionAssignment[] }> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/criterion-transcription-assignments/bulk-done`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assignmentIds, done }),
    }
  );
  return response.json();
};

export const bulkDeleteAssignments = async (
  assignmentIds: string[]
): Promise<{ deletedCount: number; skippedStartedCount: number }> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/criterion-transcription-assignments/bulk-delete`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assignmentIds }),
    }
  );
  return response.json();
};

export const fetchCandidateTranscriptions = async (
  criteriaId: string,
  filters: CandidateTranscriptionFilters = {}
): Promise<CandidateTranscription[]> => {
  const params = new URLSearchParams();
  if (filters.purposeId?.length) params.set('purpose_id', filters.purposeId.join(','));
  if (filters.yearGroups?.length)
    params.set('year_groups', filters.yearGroups.map(String).join(','));
  if (filters.onlyGroundTruth !== undefined)
    params.set('only_ground_truth', String(filters.onlyGroundTruth));
  if (filters.preferGroundTruth !== undefined)
    params.set('prefer_ground_truth', String(filters.preferGroundTruth));
  const query = params.toString();
  const response = await authenticatedFetch(
    `${API_URL}/api/marking-metrics/criteria/${criteriaId}/candidate-transcriptions${query ? `?${query}` : ''}`
  );
  const result: { candidates: CandidateTranscription[] } = await response.json();
  return result.candidates;
};

export const bulkCreateAssignments = async (
  criteriaId: string,
  userIds: string[],
  transcriptionIds: string[]
): Promise<BulkAssignmentResponse> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/criterion-transcription-assignments/bulk`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ criteriaId, userIds, transcriptionIds }),
    }
  );
  return response.json();
};

const appendJudgementSourceParams = (params: URLSearchParams, source?: AiJudgementSource): void => {
  if (!source) return;
  params.set('source', source.kind);
  if (source.kind === 'eval' && source.markingRunId) {
    params.set('marking_run_id', source.markingRunId);
  }
};

// Disagreement review endpoints
export const fetchDisagreementCandidates = async (
  criteriaId: string,
  consensusMode?: ConsensusMode,
  currentPolicyOnly?: boolean,
  source?: AiJudgementSource,
  includeAgreed?: boolean
): Promise<DisagreementCandidate[]> => {
  const params = new URLSearchParams({ criteria_id: criteriaId });
  if (consensusMode) params.set('consensus_mode', consensusMode);
  if (currentPolicyOnly) params.set('current_policy_only', 'true');
  if (includeAgreed) params.set('include_agreed', 'true');
  appendJudgementSourceParams(params, source);
  const response = await authenticatedFetch(
    `${API_URL}/api/disagreement-reviews/candidates?${params}`
  );
  return response.json();
};

export const fetchDisagreementCounts = async (
  consensusMode?: ConsensusMode,
  currentPolicyOnly?: boolean,
  source?: AiJudgementSource
): Promise<DisagreementCountsResponse> => {
  const params = new URLSearchParams();
  if (consensusMode) params.set('consensus_mode', consensusMode);
  if (currentPolicyOnly) params.set('current_policy_only', 'true');
  appendJudgementSourceParams(params, source);
  const qs = params.toString();
  const response = await authenticatedFetch(
    `${API_URL}/api/disagreement-reviews/counts${qs ? `?${qs}` : ''}`
  );
  return {
    counts: await response.json(),
    omissions: {
      unlinkedTranscriptions: Number(
        response.headers.get('X-Disagreement-Review-Unlinked-Transcriptions') || 0
      ),
      conflictingPapers: Number(
        response.headers.get('X-Disagreement-Review-Conflicting-Papers') || 0
      ),
      unresolvedEntities: Number(
        response.headers.get('X-Disagreement-Review-Unresolved-Entities') || 0
      ),
    },
  };
};

// User-vs-ground-truth disagreement review endpoints
export const fetchUserVsGtCandidates = async (
  criteriaId: string,
  userId: string,
  consensusMode?: ConsensusMode,
  currentPolicyOnly?: boolean,
  source?: AiJudgementSource
): Promise<UserVsGtCandidate[]> => {
  const params = new URLSearchParams({ criteria_id: criteriaId, user_id: userId });
  if (consensusMode) params.set('consensus_mode', consensusMode);
  if (currentPolicyOnly) params.set('current_policy_only', 'true');
  appendJudgementSourceParams(params, source);
  const response = await authenticatedFetch(
    `${API_URL}/api/user-disagreement-reviews/candidates?${params}`
  );
  return response.json();
};

export const fetchUserVsGtCounts = async (
  userId: string,
  consensusMode?: ConsensusMode,
  currentPolicyOnly?: boolean,
  source?: AiJudgementSource
): Promise<UserVsGtCountsResponse> => {
  const params = new URLSearchParams({ user_id: userId });
  if (consensusMode) params.set('consensus_mode', consensusMode);
  if (currentPolicyOnly) params.set('current_policy_only', 'true');
  appendJudgementSourceParams(params, source);
  const response = await authenticatedFetch(
    `${API_URL}/api/user-disagreement-reviews/counts?${params}`
  );
  return {
    counts: await response.json(),
    omissions: {
      unlinkedTranscriptions: Number(
        response.headers.get('X-Disagreement-Review-Unlinked-Transcriptions') || 0
      ),
      conflictingPapers: Number(
        response.headers.get('X-Disagreement-Review-Conflicting-Papers') || 0
      ),
      missingJudgements: Number(
        response.headers.get('X-Disagreement-Review-Missing-Judgements') || 0
      ),
      unresolvedEntities: Number(
        response.headers.get('X-Disagreement-Review-Unresolved-Entities') || 0
      ),
    },
  };
};

// Stylus DB endpoints
export const fetchEvalMarkingRuns = async (): Promise<EvalJudgementSourceCatalog> => {
  const response = await authenticatedFetch(`${API_URL}/api/stylus/eval-runs`);
  const data: unknown = await response.json();
  if (Array.isArray(data)) {
    return { runs: data as EvalMarkingRun[], productionFrom: null, productionTo: null };
  }
  const payload = data as Partial<EvalJudgementSourceCatalog>;
  return {
    runs: payload.runs ?? [],
    productionFrom: payload.productionFrom ?? null,
    productionTo: payload.productionTo ?? null,
  };
};

export const fetchProductionJudgementDates = async (): Promise<{
  from: string | null;
  to: string | null;
}> => {
  const response = await authenticatedFetch(`${API_URL}/api/stylus/production-dates`);
  return response.json();
};

export const fetchStylusTranscriptions = async (
  criteriaId: string,
  source?: AiJudgementSource
): Promise<StylusTranscriptionSummary[]> => {
  const params = new URLSearchParams({ criteria_id: criteriaId });
  appendJudgementSourceParams(params, source);
  const response = await authenticatedFetch(`${API_URL}/api/stylus/transcriptions?${params}`);
  return response.json();
};

export const fetchStylusJudgement = async (
  criteriaId: string,
  transcriptionId: string,
  source?: AiJudgementSource
): Promise<StylusJudgement> => {
  const params = new URLSearchParams({
    criteria_id: criteriaId,
    transcription_id: transcriptionId,
  });
  appendJudgementSourceParams(params, source);
  const response = await authenticatedFetch(`${API_URL}/api/stylus/judgement?${params}`);
  return response.json();
};

// Reviewed outputs endpoints
export const fetchReviewedOutputs = async (): Promise<ReviewedOutput[]> => {
  const response = await authenticatedFetch(`${API_URL}/api/reviewed-outputs`);
  return response.json();
};

export const createReviewedOutput = async (
  data: Omit<ReviewedOutput, 'id' | 'reviewedAt' | 'reviewedBy' | 'source'> & {
    source?: ReviewedOutput['source'];
  }
): Promise<ReviewedOutput> => {
  const response = await authenticatedFetch(`${API_URL}/api/reviewed-outputs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
};

export const deleteReviewedOutput = async (id: string): Promise<void> => {
  await authenticatedFetch(`${API_URL}/api/reviewed-outputs/${id}`, {
    method: 'DELETE',
  });
};

// Policy suggestions endpoints
export const fetchPolicySuggestions = async (criteriaId?: string): Promise<PolicySuggestion[]> => {
  const params = criteriaId ? `?criteria_id=${criteriaId}` : '';
  const response = await authenticatedFetch(`${API_URL}/api/policy-suggestions${params}`);
  return response.json();
};

export const fetchCriteriaWithFeedback = async (): Promise<string[]> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/policy-suggestions/criteria-with-feedback`
  );
  return response.json();
};

export const generatePolicySuggestion = async (
  criteriaId: string
): Promise<PolicySuggestionGenerateResult> => {
  const response = await authenticatedFetch(`${API_URL}/api/policy-suggestions/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ criteriaId }),
  });
  return response.json();
};

export const skipPolicyFeedback = async (
  reviewedOutputId: string
): Promise<{ outstandingFeedback: OutstandingPolicyFeedback[] }> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/policy-suggestions/reviews/${reviewedOutputId}/skip`,
    { method: 'POST' }
  );
  return response.json();
};

export const applyOutstandingPolicyUpdate = async (
  criteriaId: string,
  policyText: string,
  reviewedOutputIds: string[]
): Promise<{ outstandingFeedback: OutstandingPolicyFeedback[] }> => {
  const response = await authenticatedFetch(`${API_URL}/api/policy-suggestions/manual-update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ criteriaId, policyText, reviewedOutputIds }),
  });
  return response.json();
};

export const acceptPolicySuggestion = async (
  suggestionId: string,
  editedText?: string
): Promise<PolicySuggestion> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/policy-suggestions/${suggestionId}/accept`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editedText ? { suggestedPolicyText: editedText } : {}),
    }
  );
  return response.json();
};

export const dismissPolicySuggestion = async (suggestionId: string): Promise<PolicySuggestion> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/policy-suggestions/${suggestionId}/dismiss`,
    { method: 'POST' }
  );
  return response.json();
};

export const deleteSuggestionFeedback = async (suggestionId: string): Promise<PolicySuggestion> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/policy-suggestions/${suggestionId}/delete-feedback`,
    { method: 'POST' }
  );
  return response.json();
};

export const fetchStylusJudgementById = async (judgementId: string): Promise<StylusJudgement> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/stylus/judgement-by-id?judgement_id=${judgementId}`
  );
  return response.json();
};

// Airtable moderation endpoints
export const fetchAirtableModerations = async (
  criteriaId: string
): Promise<AirtableModeration[]> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/airtable-moderations?criteria_id=${criteriaId}`
  );
  return response.json();
};

// Moderation policy suggestion endpoints
export const generateModerationSuggestion = async (
  criteriaId: string
): Promise<ModerationPolicySuggestion | { noSuggestion: true }> => {
  const response = await authenticatedFetch(`${API_URL}/api/moderation-suggestions/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ criteriaId }),
  });
  return response.json();
};

export const acceptModerationSuggestion = async (
  suggestionId: string,
  editedText?: string
): Promise<ModerationPolicySuggestion> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/moderation-suggestions/${suggestionId}/accept`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editedText ? { suggestedPolicyText: editedText } : {}),
    }
  );
  return response.json();
};

export const dismissModerationSuggestion = async (
  suggestionId: string
): Promise<ModerationPolicySuggestion> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/moderation-suggestions/${suggestionId}/dismiss`,
    { method: 'POST' }
  );
  return response.json();
};

export const flagInaccurateModerations = async (
  airtableRecordIds: string[]
): Promise<{ flaggedCount: number }> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/moderation-suggestions/flag-inaccurate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ airtableRecordIds }),
    }
  );
  return response.json();
};

export const fetchGroundTruthCoverage = async (
  filters: GroundTruthCoverageFilters = {}
): Promise<GroundTruthCoverageResponse> => {
  const params = new URLSearchParams();
  const appendList = (key: string, values: string[] | undefined) => {
    if (values && values.length > 0) params.set(key, values.join(','));
  };
  appendList('criteria_id', filters.criteriaId);
  appendList('granularity', filters.granularity);
  appendList('purpose_id', filters.purposeId);
  appendList('created_by', filters.createdBy);
  if (filters.createdFrom) params.set('created_from', filters.createdFrom);
  if (filters.createdTo) params.set('created_to', filters.createdTo);
  const qs = params.toString();
  const url = `${API_URL}/api/ground-truth-metrics/criteria${qs ? `?${qs}` : ''}`;
  const response = await authenticatedFetch(url);
  return response.json();
};

export const fetchMarkingMetrics = async (
  filters: MarkingMetricsFilters = {}
): Promise<MarkingMetricsResponse> => {
  const params = new URLSearchParams();
  const appendList = (key: string, values: string[] | undefined) => {
    if (values && values.length > 0) params.set(key, values.join(','));
  };
  appendList('criteria_id', filters.criteriaId);
  appendList('granularity', filters.granularity);
  appendList('purpose_id', filters.purposeId);
  appendList('live_state', filters.liveState);
  appendList('evidence_state', filters.evidenceState);
  appendList('readiness', filters.readiness);
  appendList('coverage_state', filters.coverageState);
  if (filters.criterionQuery) params.set('criterion_query', filters.criterionQuery);
  if (filters.minAvailableGroundTruth !== undefined)
    params.set('min_available_ground_truth', String(filters.minAvailableGroundTruth));
  if (filters.minCompared !== undefined) params.set('min_compared', String(filters.minCompared));
  if (filters.minDisagreementReviews !== undefined)
    params.set('min_disagreement_reviews', String(filters.minDisagreementReviews));
  if (filters.maxDisagreementReviews !== undefined)
    params.set('max_disagreement_reviews', String(filters.maxDisagreementReviews));
  if (filters.minExactAgreement !== undefined)
    params.set('min_exact_agreement', String(filters.minExactAgreement));
  if (filters.maxExactAgreement !== undefined)
    params.set('max_exact_agreement', String(filters.maxExactAgreement));
  if (filters.minMacroPrecision !== undefined)
    params.set('min_macro_precision', String(filters.minMacroPrecision));
  if (filters.minMacroRecall !== undefined)
    params.set('min_macro_recall', String(filters.minMacroRecall));
  if (filters.minMacroF1 !== undefined) params.set('min_macro_f1', String(filters.minMacroF1));
  if (filters.maxMacroF1 !== undefined) params.set('max_macro_f1', String(filters.maxMacroF1));
  Object.entries(filters.perClassMinF1 ?? {}).forEach(([className, value]) => {
    if (value !== undefined) params.set(`min_${className.replace(' ', '_')}_f1`, String(value));
  });
  if (filters.evalFrom) params.set('eval_from', filters.evalFrom);
  if (filters.evalTo) params.set('eval_to', filters.evalTo);
  if (filters.includeArchived !== undefined)
    params.set('include_archived', String(filters.includeArchived));
  const qs = params.toString();
  const url = `${API_URL}/api/marking-metrics/criteria${qs ? `?${qs}` : ''}`;
  const response = await authenticatedFetch(url);
  return response.json();
};

export const fetchMarkingMetricsSettings = async (): Promise<MarkingMetricsSettings> => {
  const response = await authenticatedFetch(`${API_URL}/api/marking-metrics/settings`);
  return response.json();
};

export const updateMarkingMetricsSettings = async (
  settings: MarkingMetricsSettings
): Promise<MarkingMetricsSettings> => {
  const response = await authenticatedFetch(`${API_URL}/api/marking-metrics/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      exactAgreementThreshold: settings.exactAgreementThreshold,
      macroF1Threshold: settings.macroF1Threshold,
      evidenceThresholds: settings.evidenceThresholds,
      gradientAmberMargin: settings.gradientAmberMargin,
      minClassSupport: settings.minClassSupport,
    }),
  });
  return response.json();
};

export const unflagModerations = async (
  airtableRecordIds: string[]
): Promise<{ unflaggedCount: number }> => {
  const response = await authenticatedFetch(`${API_URL}/api/moderation-suggestions/unflag`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ airtableRecordIds }),
  });
  return response.json();
};

// --- Policy-version comparison ----------------------------------------------------

export const fetchCriteriaEligibility = async (): Promise<CriterionEligibilitySummary[]> => {
  const response = await authenticatedFetch(`${API_URL}/api/policy-comparison/criteria`);
  return response.json();
};

export const fetchEligibilityProgress = async (): Promise<EligibilityProgress> => {
  const response = await authenticatedFetch(`${API_URL}/api/policy-comparison/progress`);
  return response.json();
};

export const fetchCriterionComparison = async (
  criteriaId: string
): Promise<CriterionComparison> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/policy-comparison/criteria/${criteriaId}`
  );
  return response.json();
};

export const setVersionEligibility = async (
  policyId: string,
  eligible: boolean,
  reason?: string
): Promise<{ versions: PolicyVersionState[] }> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/policy-versions/${policyId}/eligibility`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      // decided_against_version is deliberately not sent: the server derives it.
      body: JSON.stringify({ eligible, reason }),
    }
  );
  return response.json();
};

export const bulkSetEligibility = async (
  criteriaId: string,
  action: BulkEligibilityAction
): Promise<{ action: string; versionsChanged: number; versions: PolicyVersionState[] }> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/policy-comparison/criteria/${criteriaId}/eligibility`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    }
  );
  return response.json();
};

// --- Policy-version pinning -----------------------------------------------------------
// Which rubric Stylus marks with. Separate from eligibility on purpose: see
// docs/specs/policy-version-pinning.md. Both return the refreshed versions, like the
// eligibility writes, so the page updates the same way after either kind of decision.

export const pinPolicyVersion = async (
  criteriaId: string,
  policyId: string,
  reason?: string
): Promise<{ pin: PolicyPin | null; versions: PolicyVersionState[] }> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/policy-comparison/criteria/${criteriaId}/pin`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ policyId, reason }),
    }
  );
  return response.json();
};

export const unpinPolicyVersion = async (
  criteriaId: string
): Promise<{ pin: PolicyPin | null; versions: PolicyVersionState[] }> => {
  const response = await authenticatedFetch(
    `${API_URL}/api/policy-comparison/criteria/${criteriaId}/pin`,
    { method: 'DELETE' }
  );
  return response.json();
};
